import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { ArrowLeft, Award, BookOpen, Clock, ListChecks, ArrowRight, Lock } from "lucide-react";

export default function FinalExamIntro() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    (async () => {
      const [{ data: c }, { data: p }] = await Promise.all([
        api.get("/curriculum"),
        api.get("/progress"),
      ]);
      setMeta(c.final_exam);
      setProgress(p);
    })();
  }, []);

  // Gate: M5-L1 (Final Exam Prep lesson) must be marked complete
  const m5LessonComplete = (progress?.lessons_completed || []).includes("m5-l1");
  const start = () => {
    if (!m5LessonComplete) return;
    nav(`/quiz/${meta.id}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-16" data-testid="final-exam-intro">
        <Link to="/dashboard" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>

        <div className="overline mb-3"><span className="red-bar" />Final practice exam</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-4">NICET Level 1 Foundation Practice Exam</h1>
        <p className="text-zinc-700 max-w-2xl leading-relaxed">
          Simulate the NICET Level 1 Foundation experience. 50 multiple-choice questions covering the full 70-hour
          curriculum. You may reference your NFPA 72 just like the real test. Take as many attempts as you need —
          your best score is saved to your transcript.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mt-10">
          <div className="card-tech p-6"><ListChecks className="w-5 h-5 text-red-600 mb-3" /><div className="font-heading text-2xl font-bold">{meta?.total_questions || 50}</div><div className="text-xs uppercase tracking-widest font-mono text-zinc-500 mt-1">Questions</div></div>
          <div className="card-tech p-6"><Clock className="w-5 h-5 text-red-600 mb-3" /><div className="font-heading text-2xl font-bold">{meta?.time_limit_minutes || 120} min</div><div className="text-xs uppercase tracking-widest font-mono text-zinc-500 mt-1">Suggested</div></div>
          <div className="card-tech p-6"><Award className="w-5 h-5 text-red-600 mb-3" /><div className="font-heading text-2xl font-bold">{meta?.passing_score_pct || 70}%</div><div className="text-xs uppercase tracking-widest font-mono text-zinc-500 mt-1">To pass</div></div>
        </div>

        {progress?.final_exam && (
          <div className="card-tech p-6 mt-6" data-testid="final-exam-best">
            <div className="overline mb-2">Your best attempt</div>
            <div className="font-heading text-3xl font-bold">{progress.final_exam.percentage}%</div>
            <div className={`text-xs font-semibold mt-1 ${progress.final_exam.passed ? "text-green-700" : "text-red-600"}`}>
              {progress.final_exam.passed ? "PASSED" : "RETAKE NEEDED"}
            </div>
          </div>
        )}

        {/* Gate: M5 Final Exam Prep must be completed before the exam unlocks */}
        {progress && !m5LessonComplete && (
          <div className="mt-10 border-l-4 border-red-600 bg-red-50 p-6 sm:p-7" data-testid="final-exam-locked">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-600 text-white grid place-items-center flex-shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="overline mb-2">Locked — exam prep required</div>
                <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">
                  Complete Module 5 first
                </h2>
                <p className="text-sm text-zinc-700 leading-relaxed mb-4">
                  The final practice exam unlocks after you finish the <strong>Final Exam Preparation</strong> lesson
                  in Module 5. It covers test-taking strategy, NFPA 72 tabbing, memory aids, and the day-of-exam plan —
                  the highest-leverage 60 minutes you'll spend before sitting the real NICET exam.
                </p>
                <Link to="/lesson/m5-l1" data-testid="final-exam-locked-cta">
                  <button className="btn-primary">
                    <BookOpen className="w-4 h-4" /> Open exam prep lesson <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 flex gap-3 flex-wrap">
          <button
            onClick={start}
            disabled={!m5LessonComplete}
            className={m5LessonComplete ? "btn-primary" : "btn-primary opacity-40 cursor-not-allowed"}
            data-testid="start-final-exam-btn"
          >
            {m5LessonComplete ? <BookOpen className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            {m5LessonComplete ? "Start exam" : "Locked"} <ArrowRight className="w-4 h-4" />
          </button>
          <Link to="/dashboard" data-testid="final-exam-back-btn">
            <button className="btn-outline">Back to dashboard</button>
          </Link>
        </div>
      </main>
    </div>
  );
}
